import React from 'react';
import PropTypes from 'prop-types';
import Downshift from 'downshift';
import styled from 'styled-components';
import { DisableProvider } from 'spark-ui/ui-kitchen/utensils/Disableable';
import InputLabel from 'spark-ui/ui-kitchen/ingredients/spices/InputLabel';
import { SelectInput } from './SelectInput';
import SelectMenu from './SelectMenu';
import { SelectItem } from './SelectItem';

const SearchStatus = styled.span`
    cursor: default;
    color: #595959;
    font-style: italic;
    font-size: 13px;
`;

const InputContainer = styled.span`
    position: relative;
`;

const defaultContainerStyle = {
    position: 'relative',
    display: 'inline-block',
    paddingRight: '10px',
    width: 'calc(100% - 10px)'
};

const containerStyles = sizeProp =>
    sizeProp
        ? {
              ...defaultContainerStyle,
              width: sizeProp
          }
        : defaultContainerStyle;

/**
 * SelectBase is an abstract and super primitive dropdown field.
 * It should only ever be used to build actual dropdown components
 * that implement the required props to get the desired behavior.
 */
const SelectBase = ({
    stateReducer,
    label,
    options,
    onChange,
    onSelect,
    onBlur,
    onInputClick,
    placeholder,
    disabled,
    selectedItemToString,
    renderSelected,
    renderInputDecoration,
    renderOption,
    size,
    optional,
    filterOptions,
    inputRef,
    noResultsText,
    initialValue,
    disabledValueReport,
    maxLength,
}) => {
    return (
        <DisableProvider disabled={disabled}>
            <Downshift
                itemToString={item => item ? selectedItemToString(item) : ''}
                onChange={values => onChange(values)}
                onSelect={onSelect}
                stateReducer={stateReducer}
                initialSelectedItem={initialValue}
            >
                {downshiftProps => {
                    const {
                        getInputProps,
                        getLabelProps,
                        getMenuProps,
                        isOpen,
                        inputValue,
                        openMenu,
                        selectedItem
                    } = downshiftProps;

                    const visibleOptions = filterOptions
                        ? options.filter(filterOptions(downshiftProps))
                        : options;

                    const disabledValue = disabledValueReport;

                    return (
                        <div style={containerStyles(size)}>
                            {label && (
                                <InputLabel {...getLabelProps()} optional={optional}>
                                    {label}
                                </InputLabel>
                            )}
                            <InputContainer onClick={disabled ? undefined : openMenu}>
                                <SelectInput
                                    ref={inputRef}
                                    {...getInputProps({
                                        onBlur: e => {
                                            onBlur && onBlur(e, downshiftProps);
                                        },
                                        onClick: e => {
                                            onInputClick && onInputClick(e, downshiftProps);
                                        },
                                        value: inputValue ? inputValue : '',
                                        placeholder,
                                        isOpen,
                                        maxLength,
                                        disabled: disabledValue === 'disableStudy' ? (selectedItem && selectedItem.length >= 20 ? true : false ) : disabled
                                    })}
                                />
                                {disabledValue === 'disableStudy' && selectedItem && selectedItem.length >= 20 ? '' : renderInputDecoration && renderInputDecoration(downshiftProps)}
                            </InputContainer>
                            {renderSelected && renderSelected(downshiftProps)}
                            {isOpen && (
                                <SelectMenu
                                    {...getMenuProps({
                                        onMouseDown: event => {
                                            event.preventDefault();
                                            event.stopPropagation();
                                        }
                                    })}
                                    hasLabel={!!label}
                                >
                                    {visibleOptions.length === 0 && (
                                        <SelectItem static>
                                            <SearchStatus>
                                                {typeof noResultsText === 'string'
                                                    ? noResultsText
                                                    : noResultsText(downshiftProps)}
                                            </SearchStatus>
                                        </SelectItem>
                                    )}
                                    {visibleOptions.map(renderOption(downshiftProps))}
                                </SelectMenu>
                            )}
                        </div>
                    );
                }}
            </Downshift>
        </DisableProvider>
    );
};

SelectBase.propTypes = {
    //Used to control downshift's state when certain events happen
    //Current examples include keeping the dropdown open on select
    //and clearing the input field on select
    stateReducer: PropTypes.func,
    label: PropTypes.string,

    //Array of possible options that will be filtered again
    //if provided a filterOptions prop. This is expected to be
    //an array of objects with "value" and "label" properties
    options: PropTypes.array.isRequired,

    //When the field value changes (selecting or removing values)
    onChange: PropTypes.func,

    //When an option is selected
    onSelect: PropTypes.func,

    //When user blurs the field
    //Only fires after user completely blurs the field
    //(i.e, not when navigating to the dropdown menu)
    onBlur: PropTypes.func,

    //When user actually clicks on the text input field
    onInputClick: PropTypes.func,

    //Input placeholder text
    placeholder: PropTypes.string,

    //Is this field disabled
    disabled: PropTypes.bool,

    //Take the selected value and return a formatted string
    //Renders as the input text value
    selectedItemToString: PropTypes.func,

    //Render prop for rendering the selected items
    //(currently used for rendering Pills on multiselects)
    //Receives all of downshift's props
    renderSelected: PropTypes.func,

    //Render an icon decoration on the text input
    //Receives all of downshift's props
    renderInputDecoration: PropTypes.func,

    //Render an individual option in the dropdown menu
    //Receives all of downshift's props
    renderOption: PropTypes.func.isRequired,

    //Function to remove available options from the dropdown's list
    //Receives all of downshift's props
    filterOptions: PropTypes.func,

    //Ref set on the text input
    inputRef: PropTypes.object,

    //What to show when there are no results found
    //If a function, it will receive all of downshift's props
    noResultsText: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),

    //Initial value for the field. For multiselects, this should be an array;
    //all values should still be objects with "value" and "label" properties
    initialValue: PropTypes.any,

    //Optional HTML maxLength for the search input
    maxLength: PropTypes.number,
};

export default SelectBase;
